import type { ApiKey, GuestLink, Site } from "./api.ts";
import type {
  CredentialRecovery,
  GuestLinkCredentialRecovery,
} from "./credential-recovery.ts";
import { relativeTime } from "./relative-time.ts";

/** One guest link as the Access page lists it. */
export interface GuestLinkRow {
  readonly link: GuestLink;
  /** The Site's name, or "Removed site" when the Site is gone. */
  readonly siteName: string;
  readonly state: "active" | "paused" | "expired" | "revoked";
  /** "Expires 12 Nov", "Never expires", "Paused, site archived", "Revoked 3 days ago". */
  readonly detail: string;
  /**
   * The browser recovery receipt that can still return this link's share URL.
   * Copy shows only when this is set: AppNotes stores no share URL, so a link
   * is copyable only while its one-time URL is recoverable in this browser.
   */
  readonly recovery: GuestLinkCredentialRecovery | null;
}

/**
 * Current links (active and paused) first, newest first; revoked and expired
 * links go to the history. Revoke shows on every current row; Copy only on a
 * row with a live receipt for an enabled Site.
 */
export function guestLinkRows(input: {
  readonly links: readonly GuestLink[];
  readonly sites: readonly Site[];
  readonly recoveries: readonly CredentialRecovery[];
  readonly now?: number;
}): {
  readonly current: readonly GuestLinkRow[];
  readonly history: readonly GuestLinkRow[];
} {
  const now = input.now ?? Date.now();
  const sites = new Map(input.sites.map((site) => [site.id, site]));
  const rows = [...input.links]
    .sort(
      (left, right) =>
        Date.parse(right.created_at) - Date.parse(left.created_at),
    )
    .map((link): GuestLinkRow => {
      const site = sites.get(link.site_id);
      const state = guestLinkState(link, site, now);
      const recovery =
        state === "active" && site?.enabled === true
          ? (input.recoveries.find(
              (candidate): candidate is GuestLinkCredentialRecovery =>
                candidate.kind === "guest_link" &&
                candidate.linkId === link.id &&
                candidate.siteId === link.site_id &&
                Date.parse(candidate.recoverableUntil) > now,
            ) ?? null)
          : null;
      return {
        link,
        siteName: site?.name ?? "Removed site",
        state,
        detail: guestLinkDetail(link, state, now),
        recovery,
      };
    });
  return {
    current: rows.filter(
      (row) => row.state === "active" || row.state === "paused",
    ),
    history: rows.filter(
      (row) => row.state === "expired" || row.state === "revoked",
    ),
  };
}

function guestLinkState(
  link: GuestLink,
  site: Site | undefined,
  now: number,
): GuestLinkRow["state"] {
  if (link.revoked_at !== null) return "revoked";
  if (link.expires_at !== null && Date.parse(link.expires_at) <= now)
    return "expired";
  return site !== undefined && !site.enabled ? "paused" : "active";
}

function guestLinkDetail(
  link: GuestLink,
  state: GuestLinkRow["state"],
  now: number,
): string {
  switch (state) {
    case "paused":
      return "Paused, site archived";
    case "revoked":
      return sinceLabel("Revoked", link.revoked_at ?? link.created_at, now);
    case "expired":
      return sinceLabel("Expired", link.expires_at ?? link.created_at, now);
    case "active":
      return link.expires_at === null
        ? "Never expires"
        : `Expires ${calendarDay(link.expires_at, now)}`;
  }
}

/** "2 revoked · 1 expired": the label of the history disclosure. */
export function guestLinkHistorySummary(
  rows: readonly Pick<GuestLinkRow, "state">[],
): string {
  const revoked = rows.filter((row) => row.state === "revoked").length;
  const expired = rows.filter((row) => row.state === "expired").length;
  return [
    revoked > 0 ? `${revoked} revoked` : null,
    expired > 0 ? `${expired} expired` : null,
  ]
    .filter((value): value is string => value !== null)
    .join(" · ");
}

/** API keys split into the rows people act on and the revoked history. */
export function apiKeyRows(keys: readonly ApiKey[]): {
  readonly current: readonly ApiKey[];
  readonly revoked: readonly ApiKey[];
} {
  return {
    current: keys.filter((key) => key.revoked_at === null),
    revoked: keys.filter((key) => key.revoked_at !== null),
  };
}

/** "Used 38 min ago", "Revoked yesterday", "Used 4 Aug": a verb and an age. */
export function sinceLabel(
  verb: string,
  value: string,
  now: number = Date.now(),
): string {
  return `${verb} ${lowerFirst(relativeTime(value, now))}`;
}

/** "Used 38 min ago"; a key never used reads "Never used". */
export function usedLabel(
  lastUsedAt: string | null,
  now: number = Date.now(),
): string {
  return lastUsedAt === null
    ? "Never used"
    : sinceLabel("Used", lastUsedAt, now);
}

/** "Sent 2 days ago", "Sent today" within the same calendar day. */
export function sentLabel(createdAt: string, now: number = Date.now()): string {
  const sent = new Date(Date.parse(createdAt));
  const today = new Date(now);
  if (
    Number.isFinite(sent.getTime()) &&
    sent.getFullYear() === today.getFullYear() &&
    sent.getMonth() === today.getMonth() &&
    sent.getDate() === today.getDate()
  )
    return "Sent today";
  return sinceLabel("Sent", createdAt, now);
}

/** "Just now" and "Yesterday" read in lower case after a verb; dates stay. */
function lowerFirst(value: string): string {
  return /^[A-Z][a-z]/u.test(value)
    ? value.charAt(0).toLowerCase() + value.slice(1)
    : value;
}

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "12 Nov", with the year only when it differs from now's. */
function calendarDay(value: string, now: number): string {
  const date = new Date(Date.parse(value));
  if (!Number.isFinite(date.getTime())) return "on an unknown date";
  const day = `${date.getDate()} ${months[date.getMonth()] ?? ""}`;
  return date.getFullYear() === new Date(now).getFullYear()
    ? day
    : `${day} ${date.getFullYear()}`;
}
